import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { catchError, concat, debounceTime, distinctUntilChanged, filter, map, Observable, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { BaseService } from 'src/app/_services/base.service';
import { environment } from '../../../environments/environment';
import { ShoppingCartService } from '../shopping-cart.service';
import {loadStripe} from '@stripe/stripe-js';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/_services/auth.service';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss']
})
export class CheckoutComponent implements OnInit {
	environment = environment;
	public tokenDetails:any;
	private stripe:any;
	private stripeElements:any;
	private cardElement:any;
	public loading:boolean=false;

	regForm: FormGroup = this.fb.group({
		company_name: ['', Validators.required],
		firstname: ['', Validators.required],
		lastname: ['', Validators.required],
		email: ['', Validators.required],
		password: ['', Validators.required],
		contact_no: ['', Validators.required],
		address_1: ['', Validators.required],
		address_2: [''],

		address: [null, Validators.required],
		// suburb: ['', Validators.required],
		// state: ['', Validators.required],
		// postcode: ['', Validators.required],

		reference_no: '',
		// username: ['', Validators.required],
		// pass: ['', Validators.required],
		// confirm_pass: ['', Validators.required],
	});


	postalCodeMinLengthTerm = 1;
	postalCodes$: Observable<any> = new Observable<any>();
	postalCodeLoading = false;
	postalCodeInput$ = new Subject<any>();

	termsAgreementFlag = false;

	isLoggedIn$: boolean = false;
	loginForm: FormGroup = this.fb.group({
		username: ['', Validators.required],
		password: ['', Validators.required],
	});

	constructor(public cartService: ShoppingCartService,
		private fb: FormBuilder,
		private baseService: BaseService,
		private authService: AuthService,
		private toastr: ToastrService,
		private titleService: Title,
		private router:Router
		) { }

	ngOnInit(): void {
		this.titleService.setTitle("Checkout")
		this.authService.userData$.subscribe(res=>{
			if(res?.authdata) {
				this.isLoggedIn$ = true;
			} else {
				this.isLoggedIn$ = false;
			}
		})

		this.setupPaymentForm();
		
		this.loadPostalCodes();
	}

	// calcCartTotal() {
	// 	this.grandTotal = this.cartService.getTotalPrice();
	// }

	loadPostalCodes() {
		this.postalCodes$ = concat(
			of([]), // default items
			this.postalCodeInput$.pipe(
				filter(res => {
					return res !== null && res.length >= this.postalCodeMinLengthTerm
				}),
				distinctUntilChanged(),
				debounceTime(800),
				tap(() => this.postalCodeLoading = true),
				switchMap(term => {
					console.log(term)
					return this.getPostCodes(term).pipe(
						catchError(() => of([])), // empty list on error
						tap(() => this.postalCodeLoading = false)
					)
				})
			)
		);
	}

	loadToPostalCodes() {
		this.postalCodes$ = concat(
			of([]), // default items
			this.postalCodeInput$.pipe(
				filter(res => {
					return res !== null && res.length >= this.postalCodeMinLengthTerm
				}),
				distinctUntilChanged(),
				debounceTime(800),
				tap(() => this.postalCodeLoading = true),
				switchMap(term => {
					console.log(term)
					return this.getPostCodes(term).pipe(
						catchError(() => of([])), // empty list on error
						tap(() => this.postalCodeLoading = false)
					)
				})
			)
		);
	}

	getPostCodes(term: any) {
		const params = {
			q: term,
		}
		return this.baseService.getPostCodeList(params, false).pipe(map(resp => {
			if (resp.Error) {
				throwError(resp.Error);
			} else {
				return resp;
			}
		}));
	}

	findInvalidControls(form: any) {
		const invalid = [];
		const controls = form.controls;
		for (const name in controls) {
			if (controls[name].invalid) {
				invalid.push(name);
			}
		}
		console.log(invalid);
		return invalid;
	}

	validatePlaceOrder() {
		if(!this.isLoggedIn$) {
			if (this.regForm.invalid) {
				this.findInvalidControls(this.regForm)
				const msg = 'Invalid Form';
				const header = 'Please fill the required fields...';
				this.toastr.error(header, msg, {
					positionClass: 'toast-top-right',
				});
				
				document.getElementById('regForm')?.classList.add('input-error');
				return false;
			} else {
				this.placeOrder()
			}
		} else {
			this.placeOrder()
		}
		return true;
	}

	placeOrder() {
		this.loading=true;
		const customerData = this.regForm.value;

		const address = this.regForm.get("address")?.value;
		customerData.suburb = address.suburb;
		customerData.postcode = address.postcode;
		customerData.state = address.state;

		this.chargeCreditCard(customerData.firstname+" "+customerData.lastname).then((paymentDetails:any)=>{
			const orderDetails = {
				"order_list": this.cartService.cart.orderList,
				"customer": customerData,
				"reference_no": this.regForm.get('reference_no')?.value,
				'payment_id':paymentDetails.paymentMethod.id,
				'payment_type':'card'
			}
			this.baseService.placeOrder(orderDetails).subscribe((res:any)=>{
				this.toastr.success('Your order was placed successfully.');
				this.loading=false;
				this.cartService.cart.empty();
				this.router.navigateByUrl("/order-confirmation");

			},(error:any)=>{
				this.toastr.error('Can not place your order. Please try again!');
				this.loading=false;
			});
		});
	}

	setupPaymentForm(){
		this.baseService.getPaymentIntent(this.cartService.cart.calcTotal()).subscribe(async (tokenResponse:any)=>{
			this.tokenDetails = tokenResponse;
			this.stripe = await loadStripe(this.tokenDetails.publicKey);
			if(this.stripe){
				const token:string = this.tokenDetails.token;
				this.stripeElements = this.stripe.elements({clientSecret: token });
				this.cardElement = this.stripeElements.create("card");
				this.cardElement.mount("#stripe-card");
			}
		});
	}

	async chargeCreditCard(customerName:string){
		const elements = this.stripeElements;
		return this.stripe.createPaymentMethod({
			type: 'card',
			card: this.cardElement,
			billing_details: {
			  // Include any additional collected billing details.
			  name: customerName,
			},
		  });

	}

	validateLogin() {
		console.log("login clicked")
		console.log(this.loginForm.value)
		if (this.loginForm.invalid) {
			this.findInvalidControls(this.loginForm)
			const msg = 'Invalid Form';
			const header = 'Please fill the required fields...';
			this.toastr.error(header, msg, {
				positionClass: 'toast-top-right',
			});
			
			document.getElementById('loginForm')?.classList.add('input-error');
		} else {
			const userData = this.loginForm.value;

			console.log("Final Login Data", userData);
			this.login(this.loginForm.get('username')?.value, this.loginForm.get('password')?.value)
		}
	}

	login(username: string, password: string) {
		this.authService.loginByHeader(username, password)
		.subscribe({
			next: (res:any) => {
				const msg = 'Success';
				const header = 'Successfully Logged In...';
				this.toastr.success(header, msg, {
					positionClass: 'toast-bottom-center',
				});
			},
			error: (err: any) => {
				const msg = 'Failed';
				const header = 'Wrong username/password. Please try again...';
				this.toastr.error(header, msg, {
					positionClass: 'toast-bottom-center',
				});
			}
		});

	}
}
